import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, UserPlus, Calendar, MapPin } from "lucide-react";
import { format } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useEventAssignments } from "@/hooks/events/useEventAssignments";
import { useUnassignStudent } from "@/hooks/events/useUnassignStudent";
import { AssignStudentDialog } from "./AssignStudentDialog";
import type { EventRow } from "@/hooks/events/useEvents";

interface Props {
  event: EventRow;
}

export const EventAssignmentsPanel = ({ event }: Props) => {
  const { data: rows = [], isLoading } = useEventAssignments(event.id);
  const unassign = useUnassignStudent();
  const [assignOpen, setAssignOpen] = useState(false);
  const [confirmId, setConfirmId] = useState<{ id: string; code: string } | null>(null);

  return (
    <div className="border rounded-lg bg-card">
      <div className="p-4 border-b">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold truncate">{event.name}</h2>
            <div className="flex flex-wrap gap-3 mt-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {format(new Date(event.start_at), "d MMM yyyy HH:mm")}
                {event.end_at && ` - ${format(new Date(event.end_at), "HH:mm")}`}
              </span>
              {event.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {event.location}
                </span>
              )}
            </div>
            {event.description && (
              <p className="text-sm text-muted-foreground mt-2">{event.description}</p>
            )}
          </div>
          <Button onClick={() => setAssignOpen(true)} className="shrink-0">
            <UserPlus className="h-4 w-4" /> Assign นักเรียน
          </Button>
        </div>
      </div>

      <div className="p-4">
        <h3 className="text-sm font-medium mb-2">นักเรียนที่ลงทะเบียน ({rows.length})</h3>
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>รหัส</TableHead>
                <TableHead>ชื่อ-นามสกุล</TableHead>
                <TableHead>มอบหมายโดย</TableHead>
                <TableHead>วันที่</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">กำลังโหลด...</TableCell></TableRow>
              )}
              {!isLoading && rows.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">ยังไม่มีนักเรียน</TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">{r.fishsix_student_code}</TableCell>
                  <TableCell>
                    {r.student_first_name ?? ""} {r.student_last_name ?? ""}
                    {r.student_nickname ? ` (${r.student_nickname})` : ""}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.assigned_by_name ?? "-"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {format(new Date(r.assigned_at), "d MMM yyyy HH:mm")}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setConfirmId({ id: r.id, code: r.fishsix_student_code! })}
                      disabled={!r.fishsix_student_code}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <AssignStudentDialog open={assignOpen} onOpenChange={setAssignOpen} eventId={event.id} />

      <AlertDialog open={!!confirmId} onOpenChange={(v) => !v && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ยกเลิกการมอบหมาย?</AlertDialogTitle>
            <AlertDialogDescription>
              ระบบจะคืน 1 ครั้งให้นักเรียนโดยอัตโนมัติ
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!confirmId) return;
                await unassign.mutateAsync({
                  assignmentId: confirmId.id,
                  fishsixCode: confirmId.code,
                  eventId: event.id,
                });
                setConfirmId(null);
              }}
            >
              ยืนยัน
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
